import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useStripe } from '../contexts/StripeContext';
import { PaymentForm } from '../components/payment/PaymentForm';
import { createPaymentIntent } from '../api/paymentApi';

export function PaymentPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { stripe, isLoading: stripeLoading, error: stripeError } = useStripe();

  // Payment intent state
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [isLoadingPayment, setIsLoadingPayment] = useState(true);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Ref to prevent double initialization in React StrictMode
  const initRef = useRef({ started: false, completed: false });

  // Fetch payment intent when bookingId is available
  useEffect(() => {
    if (!bookingId || initRef.current.started) return;
    initRef.current.started = true;

    const fetchPaymentIntent = async () => {
      try {
        const result = await createPaymentIntent({ bookingId });
        setClientSecret(result.clientSecret);
        setPaymentId(result.paymentId);
        setAmount(result.amount);
        initRef.current.completed = true;
      } catch (err) {
        setPaymentError(err instanceof Error ? err.message : 'Failed to initialize payment');
      } finally {
        setIsLoadingPayment(false);
      }
    };

    fetchPaymentIntent();
  }, [bookingId]);

  const handleSuccess = (id: string) => {
    navigate(`/payment/success?paymentId=${id}`);
  };

  const handleError = (error: string) => {
    // Log the error but don't set paymentError - let PaymentForm handle display
    // This prevents the full-page error state from showing for card declines
    console.error('Payment error:', error);
    // Only set paymentError for initialization-level errors, not card processing errors
    // Card errors are displayed by PaymentForm's own error state
  };

  // Show loading while Stripe or payment intent is loading
  if (stripeLoading || isLoadingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400" />
          <p className="mt-4 text-gray-600 dark:text-slate-400">Loading payment...</p>
        </div>
      </div>
    );
  }

  // Show error if Stripe failed to load
  if (stripeError || !stripe) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-slate-900">
        <div className="max-w-md p-8 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/30 text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">{stripeError || 'Payment unavailable'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-900 dark:text-slate-100"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show error if no booking ID
  if (!bookingId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-slate-900">
        <div className="max-w-md p-8 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/30 text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">No booking specified</p>
          <button
            onClick={() => navigate('/bookings')}
            className="mt-4 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600"
          >
            View Bookings
          </button>
        </div>
      </div>
    );
  }

  // Show error if payment intent failed
  if (paymentError || !clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-slate-900">
        <div className="max-w-md p-8 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/30 text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">{paymentError || 'Failed to load payment'}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Elements options with clientSecret
  const elementsOptions: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#2563eb',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#dc2626',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px',
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-12 px-4">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold text-center mb-2 text-gray-900 dark:text-slate-100">Complete Payment</h1>
        <p className="text-gray-600 dark:text-slate-400 text-center mb-8">Enter your payment details below</p>

        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/30 p-6">
          <Elements stripe={stripe} options={elementsOptions}>
            <PaymentForm
              paymentId={paymentId!}
              amount={amount}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
}
